import { queryNoTracking, update } from './DataService';
import { showDialog } from './HelperService';
import { generateTemplateContent } from './TemplateService';
import { Helper } from './JobService';
import { getContext, getContextData } from '../services/EntityContextService';
import { SmsDlg } from '../dialogs/SmsDlg';
import React from 'react';
import CustomerChatView from '../views/CustomerChatView';

const query_NormalisePhoneNumber = queryNoTracking("NormalisePhoneNumber");
const query_LocalisePhoneNumber = queryNoTracking("LocalisePhoneNumber");
const query_ChatMessages = queryNoTracking('Chat_Messages');
const query_GetCustomerServiceWindow = queryNoTracking('WhatsAppIntegration_GetCustomerServiceWindow');
const query_TemplatesByType = queryNoTracking('TemplatesByType');
const query_WhatsAppConfigs = queryNoTracking('WhatsAppIntegration_GetConfigurations');
const query_ContactsMatch = queryNoTracking('Contacts_Match', undefined, true);

async function fileToBase64(file) {
    if (!file) return null;
    const r = new FileReader();
    const base64 = await new Promise((res, rej) => {
        r.onload = () => res(String(r.result).split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
    });
    return base64;
}
function CustomerChatModal(props) {
    const headerTitle =
        `Customer Chat with ${props.contactName ?? props.phoneNumber} ` +
        `(${props.phoneNumber ?? props.filterContactPoint ?? ''})`;

    const close = (result = { canceled: true }) => {
        if (typeof props.onClose === 'function') return props.onClose(result);
        if (typeof props.close === 'function') return props.close(result);
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.35)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) close();
            }}
        >
            <div
                className="customer-chat-dialog"
                style={{
                    background: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <div
                    style={{
                        padding: '10px 14px',
                        fontWeight: 600,
                        borderBottom: '1px solid var(--acc-border, #ddd)',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <span style={{ flex: 1 }}>{headerTitle}</span>
                    <button
                        className="acc-btn"
                        onClick={() => close()}
                        style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1 }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className="customer-chat-body" style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
                    <CustomerChatView
                        initialFilters={{
                            contactPoint: props.filterContactPoint,
                            contactId: props.contactId,
                            jobId: props.jobId ?? null,
                            channel: props.channel ?? null
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * API Calls required for Business Onboarding
 */
export function initialiseWhatsAppOnboarding(code, displayName) {
    return update('OnboardWhatsAppBusiness', {
        Code: code,
        DisplayName: displayName
    });
}

export async function setWhatsAppIntegrationEnabled(isEnabled) {
    return update('SetWhatsAppIntegrationEnabledStatus', { IsEnabled: isEnabled }, false);
}

export async function saveWhatsAppAccount(account) {
    return update('SaveWhatsAppAccount', account, false);
}

export async function deleteWhatsAppAccount(accountId) {
    return update('DeleteWhatsAppAccount', { Guid: accountId }, false);
}

export async function saveWhatsAppPhoneNumberConfig(config) {
    return update('SaveWhatsAppPhoneNumberConfig', config, false);
}

export async function deleteWhatsAppPhoneNumberConfig(configId) {
    return update('DeleteWhatsAppPhoneNumberConfig', { Guid: configId }, false);
}

/**
 * Try to find a single contact match for a phone number.
 * Returns the LeadMatch object when exactly one match is found; otherwise null.
 */
export async function findSingleContactMatchByPhone(contactPoint) {
    try {
        // 1) Normalise to E.164 first
        const normalised = await query_NormalisePhoneNumber.getFirstOrDefault({ phoneNumber: contactPoint });
        const e164 = normalised?.Normalised ?? contactPoint;

        // helper to hit Contacts_Match and only accept a single match
        const tryMatch = async (phone) => {
            const res = await query_ContactsMatch.getAll({
                Mobile: phone, HomePhone: phone, WorkPhone: phone
            });
            return Array.isArray(res) ? res : null;
        };

        // 2) Try exact E.164
        let list = await tryMatch(e164);

        // 3) Derive local forms from E.164 and try both
        const localised = await query_LocalisePhoneNumber.getFirstOrDefault({ phoneNumber: e164 });
        const nat = localised?.LocalNumber;

        if (nat) {
            // try 0 + national (e.g. 04xxxxxxxx) then bare national (e.g. 4xxxxxxxx)
            list = list.concat(await tryMatch(nat.startsWith('0') ? nat : `0${nat}`));
            list = list.concat(await tryMatch(nat));
        }

        // 4) As a last resort, try the raw input exactly as typed (covers odd DB entries)
        if (contactPoint && contactPoint !== e164) {
            list = list.concat(await tryMatch(contactPoint));
        }

        // dedupe by ContactID (fallback to ID or name string)
        const seen = new Set();
        const unique = [];
        for (const m of list) {
            const key = m.ContactID ?? m.ID ?? `${m.FirstName}|${m.LastName}`;
            if (!seen.has(key)) { seen.add(key); unique.push(m); }
        }
        // nothing found
        if (!unique.length) return null;
        // preserve legacy shape if only one
        return unique.length === 1 ? unique[0] : unique;
    } catch (err) {
        console.error("Contacts_Match failed", err);
        return null;
    }
}

// --- helpers (top of file or near buildWhatsAppPropsForChat) ---
const _ctxCache = new Map(); // key: `${type}:${id}` -> resolved ctx

async function safeGetContextData(context) {
    if (!context?.id || !context?.type) return {};
    const key = `${context.type}:${context.id}`;
    if (_ctxCache.has(key)) return _ctxCache.get(key);
    const data = await getContextData(context);
    _ctxCache.set(key, data ?? {});
    return data ?? {};
}

async function resolveContext({ contextHint, chat, preferPage = true }) {
    // 1) explicit override from caller wins
    if (contextHint?.id && contextHint?.type) {
        return safeGetContextData(contextHint);
    }

    // 2) page context (only if requested)
    const parent = preferPage ? getContext?.() : null;
    if (parent?.id && parent?.type) {
        return safeGetContextData(parent);
    }

    // 3) chat-derived contact
    if (chat?.ContactID) {
        return safeGetContextData({ id: chat.ContactID, type: 'Contact' });
    }

    // 4) quick phone→contact single match (best-effort, non-fatal)
    //    (kept fast by caching inside Contacts_Match and _ctxCache)
    if (chat?.ContactPoint) {
        const match = await findSingleContactMatchByPhone(chat.ContactPoint);
        if (match?.ContactID) {
            return safeGetContextData({ id: match.ContactID, type: 'Contact' });
        }
    }

    // 5) nothing found, return empty context (no flags)
    return {};
}

/**
 * Build ChatDlg props for an embedded WhatsApp conversation
 * @param {{ chat: any, title?: string, context?: { id: number|string, type: string }, preferPageContext?: boolean }} opts
 */
export async function buildWhatsAppPropsForChat(opts) {
    const { chat, title, context: contextHint, preferPageContext = true } = opts;
    if (!chat) return null;

    const chatId = chat.ChatID;

    // NEW: explicit, safe, layered resolution
    const ctx = await resolveContext({ contextHint, chat, preferPage: preferPageContext });

    const configs = await query_WhatsAppConfigs.getAllWithCache();
    const accountId = configs?.[0]?.Accounts?.[0]?.ID;
    const businessUnitId = configs?.[0]?.BusinessUnitSettings?.[0]?.ID;

    // Queries
    const getMessages = () => query_ChatMessages.getAllAsQueryable({ chatId });
    const getCustomerServiceWindow = () =>
        query_GetCustomerServiceWindow.getFirstOrDefault({ chatID: chatId });

    // Templates (flags read from ctx only; empty ctx == all false)
    const getTemplates = () => {
        const hasLinkedJob = Array.isArray(chat.LinkedJobs) && chat.LinkedJobs.length > 0;
        const flags = {
            activity: !!ctx.ActivityID,
            job: !!ctx.JobID || hasLinkedJob,
            opportunity: !!ctx.OpportunityID,
            invoice: !!ctx.InvoiceID,
            supply: !!ctx.PurchaseOrderID,
            statement: !!ctx.StatementID,
            webLead: !!ctx.WebLeadID
        };
    return query_TemplatesByType.getAllAsQueryable({ type: 'WhatsApp Template', ...flags });
};

    const genContent = async (templateID, opts = {}) => {
        const overrideJobId = opts.jobId ?? null;
        const [first] = await generateTemplateContent({
            UseBody: true,
            TemplateID: templateID,
            ContactID: ctx.ContactID ?? chat.ContactID ?? null,
            JobID: overrideJobId ?? ctx.JobID ?? null,
            ActivityID: ctx.ActivityID ?? null,
            OpportunityID: ctx.OpportunityID ?? null,
            InvoiceID: ctx.InvoiceID ?? null,
            StatementID: ctx.StatementID ?? null,
            SupplyJobID: ctx.PurchaseOrderID ?? null,
            WebLeadID: ctx.WebLeadID ?? null,
        });
        return first;
    };

    const markAsRead = async (messageID) => update('Chat_Message_MarkAsRead', { MessageID: Number(messageID) });
    const markAsResent = async (messageID) => update('Chat_Message_MarkAsResent', { MessageID: Number(messageID) });

    const saveMessage = async (message) => {
        const overrideJobId = message?.JobID ?? null;
        let attachment = null;
        if (message.Attachment?.file) {
            attachment = {
                Name: message.Attachment.name,
                MimeType: message.Attachment.type,
                Base64: await fileToBase64(message.Attachment.file)
            };
        }
        return Helper.sendWhatsAppMessage(
            chatId,
            message.Type,
            message.TemplateID,
            message.ParentID,
            message.ReplyToID,
            accountId,
            businessUnitId,
            message.CustomMessage,
            attachment,
            message.IsInternal,
            message.MentionedRepresentativeIds,
            ctx.ContactID ?? chat.ContactID ?? null,
            (overrideJobId ?? ctx.JobID) ?? null,
            ctx.ActivityID ?? null,
            ctx.OpportunityID ?? null,
            ctx.InvoiceID ?? null,
            ctx.PurchaseOrderID ?? null,
            ctx.StatementID ?? null,
            ctx.WebLeadID ?? null,
            false
        );
    };

    const sendViaSms = async (body) => {
        const result = await showDialog(<SmsDlg context={ctx} info={{ To: [{ Mobile: chat.ContactPoint }] }} defaultBody={body} />)
        
        if (!result.canceled) {
            Helper.sendSms(
                result.to.join(","),
                result.Body,
                result.ContactID,
                result.JobID,
                result.OpportunityID,
                result.ActivityID,
                result.TemplateID,
                result.DeliveryReceipt,
                result.InvoiceID
            );
            
            return true;
        }
        
        return false;
    }

    return {
        referenceNo: chat.ID,
        chatID: chat.ChatID,
        subject: title || (chat.Subject || `Chat with ${chat.ContactPointDisplayName}`),
        status: chat.Status,
        contactPoint: chat.ContactPoint,
        contactID: chat.ContactID ?? null,
        contactName: chat.ContactName ?? null,
        openDate: chat.OpenDate,
        closeDate: chat.CloseDate,
        archivedDate: chat.ArchivedAt,
        archivedBy: chat.ArchivedByRepresentativeName,
        linkedJobs: chat.LinkedJobs,
        getPossibleContactMatch: () => findSingleContactMatchByPhone(chat.ContactPoint),
        query: getMessages,
        serviceWindowQuery: getCustomerServiceWindow,
        templateQuery: getTemplates,
        generateTemplateContent: genContent,
        saveMessage,
        alternateChannelSaveMessage: [{
            channel: "SMS",
            method: sendViaSms
        }],
        markAsRead,
        markAsResent,
        ctx,
    };
}

export async function openCustomerChatDialog(contactId, phoneNumber, contactName) {
    // Normalize to E.164 so it matches how chats store ContactPoint
    let filterContactPoint = null;
    if (phoneNumber) {
        try {
            const norm = await query_NormalisePhoneNumber.getFirstOrDefault({ phoneNumber });
            filterContactPoint = norm?.Normalised ?? phoneNumber;
        } catch {
            filterContactPoint = phoneNumber;
        }
    }

    const parent = getContext?.();
    const ctx = parent?.id && parent?.type ? await getContextData(parent) : {};
    const jobId = ctx?.JobID ?? null;

    return showDialog(
        <CustomerChatModal
            contactId={contactId}
            phoneNumber={phoneNumber}
            filterContactPoint={filterContactPoint}
            contactName={contactName}
            jobId={jobId}
            channel="whatsapp"
        />
    );
}

/**
 * Send Test WhatsApp message Call
 */
export async function sendWhatsAppTestMessage(payload) {
    return await update('SendWhatsAppTestMessage', payload);
}

